import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Zap, CreditCard, Smartphone, Search, Stethoscope, Heart,
  FileText, CheckSquare, Square, X, Check, AlertCircle,
  User, Hash, MapPin, Repeat, Calendar, Upload, Trash2
} from 'lucide-react';
import './GerarCobranca.css';
import { createAsaasPayment } from '../services/asaasApi';
import { apiFetch, createCpflCharge } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { modalidadesPorUsuario } from '../services/modalidades';
import PixCobrancaTabs from '../components/PixCobrancaTabs';

const methods = [
  { id: 'cpfl',  label: 'CPFL',          desc: 'Débito direto em conta de luz', Icon: Zap,        iconClass: 'icon-cpfl'  },
  { id: 'asaas', label: 'Asaas',         desc: 'Boleto e cartão de crédito',    Icon: CreditCard, iconClass: 'icon-asaas' },
  { id: 'pix',   label: 'Pix Santander', desc: 'Pix recorrente',               Icon: Smartphone, iconClass: 'icon-pix'   },
];

const asaasPaymentTypes = [
  { id: 'BOLETO',        label: 'Boleto Bancário',   icon: FileText   },
  { id: 'CREDIT_CARD',   label: 'Cartão de Crédito', icon: CreditCard },
  { id: 'PIX',           label: 'PIX',               icon: Smartphone },
];

// Códigos de produto CPFL (CadastrarCobranca -> Produto, 6 posições)
const produtosCpfl = [
  { codigo: 'Y615', label: 'Humano (Y615)' },
  { codigo: 'Y646', label: 'Humano (Y646)' },
  { codigo: 'Y645', label: 'Pet (Y645)' },
  { codigo: 'Y647', label: 'Humano + Pet (Y647)' },
];

// Sugere um produto CPFL com base no plano já cadastrado do cliente.
// Sempre editável pelo usuário no formulário — isto é só um ponto de partida.
const sugerirProdutoCpfl = (planoNome = '') => {
  const p = (planoNome || '').toLowerCase();
  if (p.includes('humano') && p.includes('pet')) return 'Y647';
  if (p.includes('pet')) return 'Y645';
  if (p.includes('humano')) return 'Y615';
  return '';
};

const opcoesAtendimento = [
  { id: 'odontologia', label: 'Odontologia',    Icon: Stethoscope },
  { id: 'consulta',    label: 'Consulta Médica', Icon: Heart       },
];

const tipoDocumentoOptions = [
  { value: 'contrato', label: 'Contrato' },
  { value: 'autorizacao', label: 'Autorização' },
  { value: 'outros', label: 'Outros' },
];

const mapModalidade = (type) => {
  if (!type) return null;
  const map = {
    'cpfl':          'cpfl',
    'asaas':         'asaas',
    'pix':           'pix',
    'pix_automatico':'pix',
    'CPFL':          'cpfl',
    'Asaas':         'asaas',
    'Pix':           'pix',
  };
  return map[type] || null;
};

function normalizeCliente(raw) {
  if (!raw) return null;

  const isAsaas = raw._source === 'asaas' || raw.payment_type === 'asaas';

  return {
    name:      raw.full_name || raw.name || '',
    cpf:       raw.cpf || raw.cpfCnpj || '',
    matricula: isAsaas ? raw.id : (raw.cpfl_installation_number || raw.matricula || ''),
    // PN (Parceiro de Negócio) — obrigatório para CadastrarCobranca na CPFL.
    // Salvo no cadastro local como cpfl_account_number (ver routes/entities/clients.js).
    pn:        raw.cpfl_account_number || raw.pn || '',
    cidade:    isAsaas
      ? `${raw.cityName || ''}${raw.state ? '/' + raw.state : ''}`
      : (raw.city ? `${raw.city}${raw.state ? '/' + raw.state : ''}` : (raw.cidade || '')),
    plano:     raw.plan_name || raw.plano || '',
    valor:     Number(raw.plan_value ?? raw.titular_plano_valor ?? raw.valor) || 0,
    type:      raw.payment_type || raw.type || (isAsaas ? 'asaas' : 'cpfl'),
    id:        raw.id || raw.asaas_customer_id || null,
    asaas_customer_id: raw.asaas_customer_id || null,
    asaasId:   isAsaas ? raw.id : null,
    cityName:  raw.cityName || '',
    state:     raw.state || '',
  };
}

// Gera um código de identificação único para a cobrança CPFL (campo Identificacao, até 20 chars).
// Formato: CPF (11) + timestamp (9 últimos dígitos) = 20 chars.
const gerarIdentificacaoCpfl = (cpf) => {
  const cpfLimpo = (cpf || '').replace(/\D/g, '').padEnd(11, '0').slice(0, 11);
  const sufixo = Date.now().toString().slice(-9);
  return `${cpfLimpo}${sufixo}`.slice(0, 20);
};

const getTodayFormatted = () => {
  const hoje = new Date();
  const dd = String(hoje.getDate()).padStart(2, '0');
  const mm = String(hoje.getMonth() + 1).padStart(2, '0');
  const yyyy = hoje.getFullYear();
  return { iso: `${yyyy}-${mm}-${dd}`, display: `${dd}/${mm}/${yyyy}` };
};

const emptyForm = { client: '', amount: '', dueDate: getTodayFormatted().iso, description: '', parcelas: '' };

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
};

const formatBytes = (bytes) => {
  if (!bytes) return '0 KB';
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

export default function GerarCobranca() {
  const { user: usuarioLogado } = useAuth();
  const modalidadesCobranca = modalidadesPorUsuario(usuarioLogado);
  const metodosVisiveis = methods.filter((m) => modalidadesCobranca.includes(m.id));
  const modalidadeUnica = modalidadesCobranca.length === 1 ? modalidadesCobranca[0] : null;
  const [avisoModalidade, setAvisoModalidade] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const [selected, setSelected] = useState(modalidadeUnica);

  // Seleciona a única modalidade permitida assim que as permissões carregam.
  useEffect(() => {
    if (modalidadeUnica) setSelected(modalidadeUnica);
  }, [modalidadeUnica]);
  const [form,             setForm]             = useState(emptyForm);
  const [errors,           setErrors]           = useState({});
  const [categoria,        setCategoria]        = useState('plano');
  const [subOpcao,         setSubOpcao]         = useState(null);
  const [tipoCobranca,     setTipoCobranca]     = useState('indeterminada');
  const [showModal,        setShowModal]        = useState(false);
  const [success,          setSuccess]          = useState(false);
  const [showSuccessCpfl,  setShowSuccessCpfl]  = useState(false);
  const [loading,          setLoading]          = useState(false);
  const [asaasPaymentType, setAsaasPaymentType] = useState('BOLETO');

  // ── CPFL: produto e anexos reais (substituem os mocks) ──
  const [produtoCpfl,      setProdutoCpfl]      = useState('');
  const [anexosCpfl,       setAnexosCpfl]       = useState([]); // File[]

  const [dueDateDisplay,   setDueDateDisplay]   = useState(getTodayFormatted().display);
  const [searchQuery,      setSearchQuery]      = useState('');
  const [searchResults,    setSearchResults]    = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searching,        setSearching]        = useState(false);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);

  // ⚠️ Antes esse efeito só limpava o sessionStorage/localStorage sem
  // nunca ler o cliente que Clients.js salvou ali — por isso o botão "$"
  // nunca pré-preenchia nada. Agora lê primeiro (state do navigate, depois
  // sessionStorage, depois localStorage, nessa ordem de prioridade) e só
  // limpa depois de já ter usado.
  useEffect(() => {
    let clienteSalvo = null;
    try {
      const doState = location.state?.client;
      const doSession = sessionStorage.getItem('gc_cliente');
      const doLocal = localStorage.getItem('clienteGerarCobranca');
      clienteSalvo = doState || (doSession ? JSON.parse(doSession) : null) || (doLocal ? JSON.parse(doLocal) : null);
    } catch (err) {
      console.error('Erro ao ler cliente salvo para pré-preenchimento:', err);
    }
    if (clienteSalvo) {
      handleSelecionarCliente(clienteSalvo);
      console.log('✅ Cliente pré-carregado via $:', clienteSalvo.name || clienteSalvo.full_name);
    }
    sessionStorage.removeItem('gc_cliente');
    localStorage.removeItem('clienteGerarCobranca');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBuscarCliente = async () => {
    if (!searchQuery.trim()) {
      setErrors({ search: 'Digite um nome, CPF ou matrícula' });
      alert('Por favor, digite um nome, CPF ou matrícula para buscar.');
      return;
    }

    setSearching(true);
    setErrors({});

    try {
      console.log('🔍 Buscando cliente:', searchQuery);
      const response = await apiFetch(`/clients?search=${encodeURIComponent(searchQuery)}&limit=10`);
      const clientesBrutos = response.data || response.clients || [];

      const clientes = clientesBrutos.filter(
        (c) => c.cpf && String(c.cpf).trim() !== ''
      );

      console.log('🔍 Clientes encontrados (com CPF):', clientes);

      if (clientes.length === 0) {
        alert('Nenhum cliente encontrado. Verifique os dados digitados.');
        setSearchResults([]);
        setShowSearchResults(false);
      } else {
        setSearchResults(clientes);
        setShowSearchResults(true);
      }
    } catch (err) {
      console.error('❌ Erro ao buscar cliente:', err);
      alert('Erro ao buscar cliente. Tente novamente.');
    } finally {
      setSearching(false);
    }
  };

  const handleSelecionarCliente = (clienteRaw) => {
    const cliente = normalizeCliente(clienteRaw);

    // perfil restrito a certas modalidades: não deixa escolher cliente de outra modalidade
    const modalidadeDoCliente = mapModalidade(cliente.type);
    if (modalidadeDoCliente && !modalidadesCobranca.includes(modalidadeDoCliente)) {
      const permitidasTxt = methods.filter((m) => modalidadesCobranca.includes(m.id)).map((m) => m.label).join(' e ');
      const nomeDele = (methods.find((m) => m.id === modalidadeDoCliente) || {}).label || modalidadeDoCliente;
      setAvisoModalidade(`Este cliente é ${nomeDele}. Seu perfil só gera cobrança via ${permitidasTxt}.`);
      return;
    }
    setAvisoModalidade('');

    console.log('✅ Cliente selecionado:', cliente);

    setClienteSelecionado(cliente);
    setForm((prev) => ({
      ...prev,
      client: cliente.name,
      // valor do cadastro vem preenchido (continua editável); sem valor, fica em branco
      ...(cliente.valor > 0 ? { amount: String(cliente.valor) } : {}),
    }));
    setSearchQuery(`${cliente.name} (${cliente.cpf})`);
    setShowSearchResults(false);
    setSearchResults([]);

    const modalidade = mapModalidade(cliente.type);
    if (modalidade) {
      setSelected(modalidade);
    }
    if (modalidade === 'cpfl') {
      setProdutoCpfl(sugerirProdutoCpfl(cliente.plano));
    }
  };

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: null }));
  };

  const handleDueDateChange = (e) => {
    let v = e.target.value.replace(/\D/g, '').slice(0, 8);
    if (v.length >= 5) v = `${v.slice(0, 2)}/${v.slice(2, 4)}/${v.slice(4)}`;
    else if (v.length >= 3) v = `${v.slice(0, 2)}/${v.slice(2)}`;
    setDueDateDisplay(v);
    if (v.length === 10) {
      const [dd, mm, yyyy] = v.split('/');
      handleChange('dueDate', `${yyyy}-${mm}-${dd}`);
    } else {
      handleChange('dueDate', '');
    }
  };

  const handleAdicionarAnexos = (e) => {
    const novos = Array.from(e.target.files || []);
    if (novos.length === 0) return;
    const novosComMeta = novos.map((file) => ({
      file,
      tipo: 'outros',
      enviar: true,
      previewUrl: URL.createObjectURL(file),
    }));
    setAnexosCpfl((prev) => [...prev, ...novosComMeta]);
    setErrors((p) => ({ ...p, anexos: null }));
    e.target.value = ''; // permite re-selecionar o mesmo arquivo depois de removê-lo
  };

  const handleRemoverAnexo = (index) => {
    setAnexosCpfl((prev) => {
      const alvo = prev[index];
      if (alvo?.previewUrl) URL.revokeObjectURL(alvo.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleTipoAnexoChange = (index, tipo) => {
    setAnexosCpfl((prev) => prev.map((a, i) => (i === index ? { ...a, tipo } : a)));
  };

  const handleEnviarAnexoChange = (index, enviar) => {
    setAnexosCpfl((prev) => prev.map((a, i) => (i === index ? { ...a, enviar } : a)));
  };
  // Busca no FTP os anexos que a CPFL já tem pra esse cliente, baixa de
  // verdade e adiciona na MESMA lista de documentos — sem precisar de
  // nenhum clique. Fica desmarcado (enviar:false) por padrão.
  const anexosFtpBuscadosRef = useRef(new Set());
  useEffect(() => {
    const matricula = clienteSelecionado?.matricula || clienteSelecionado?.cpfl_installation_number;
    if (!matricula || selected !== 'cpfl' || anexosFtpBuscadosRef.current.has(matricula)) return;
    anexosFtpBuscadosRef.current.add(matricula);
    (async () => {
      try {
        const r = await apiFetch(`/api/cpfl/anexos-instalacao/${encodeURIComponent(matricula)}`);
        const encontrados = r.arquivos || [];
        if (encontrados.length === 0) return;
        const novos = [];
        for (const arq of encontrados) {
          try {
            const resp = await fetch(`/api/cpfl/anexo-arquivo?empresa=${encodeURIComponent(arq.empresa)}&nome=${encodeURIComponent(arq.nome)}`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            const blob = await resp.blob();
            const file = new File([blob], arq.nome, { type: blob.type });
            novos.push({ file, tipo: 'autorizacao', enviar: false, previewUrl: URL.createObjectURL(file) });
          } catch (err) {
            console.error(`Erro ao baixar anexo ${arq.nome}:`, err);
          }
        }
        if (novos.length > 0) setAnexosCpfl((prev) => [...prev, ...novos]);
      } catch (err) {
        console.error('Erro ao buscar anexos do FTP:', err);
      }
    })();
  }, [clienteSelecionado, selected]);

    const validate = () => {
    const e = {};
    if (!clienteSelecionado) e.client = 'Busque e selecione um cliente';

    const valorTotal = parseFloat(form.amount);

    if (!valorTotal || valorTotal <= 0) {
      e.amount = 'Informe um valor válido';
    } else if (tipoCobranca === 'determinada' && form.parcelas && parseInt(form.parcelas) > 1) {
      // Validação específica para parcelamento no Boleto
      const valorParcela = valorTotal / parseInt(form.parcelas);
      if (asaasPaymentType === 'BOLETO' && valorParcela < 10) {
        e.amount = `O valor mínimo por parcela no Boleto é R$ 10,00. (Atual: R$ ${valorParcela.toFixed(2)})`;
      } else if (valorParcela < 5) {
        e.amount = 'O valor mínimo por parcela é R$ 5,00';
      }
    } else {
      // Validação para cobrança avulsa (1x) ou recorrente
      if (asaasPaymentType === 'BOLETO' && valorTotal < 10) {
        e.amount = 'O valor mínimo para Boleto é R$ 10,00';
      } else if (valorTotal < 5) {
        e.amount = 'O valor mínimo é R$ 5,00';
      }
    }

    if (categoria === 'atendimento' && !subOpcao)
      e.subOpcao = 'Selecione o tipo de atendimento';

    if (tipoCobranca === 'determinada' && (!form.parcelas || Number(form.parcelas) < 1))
      e.parcelas = 'Informe a quantidade de meses';

    if (selected === 'cpfl') {
      if (!clienteSelecionado?.pn) {
        e.client = 'Este cliente não tem PN (Parceiro de Negócio) cadastrado. Edite o cadastro antes de gerar a cobrança.';
      }
      if (!clienteSelecionado?.matricula) {
        e.client = 'Este cliente não tem Instalação (UC) cadastrada. Edite o cadastro antes de gerar a cobrança.';
      }
      if (!produtoCpfl) {
        e.produtoCpfl = 'Selecione o produto de cobrança';
      }
      if (anexosCpfl.length === 0) {
        e.anexos = 'Anexe pelo menos um documento (obrigatório pela CPFL)';
      }
    }

    return e;
  };

  const handleProximo = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      if (errs.client) {
        alert(errs.client === 'Busque e selecione um cliente antes de continuar.' ? errs.client : errs.client);
      }
      return;
    }
    setShowModal(true);
  };

  // ✅ Garante que o cliente existe no Asaas
  const ensureCustomerInAsaas = async (cliente) => {
    // Se já tem asaas_customer_id, retorna ele
    if (cliente.asaas_customer_id && cliente.asaas_customer_id.startsWith('cus_')) {
      console.log('✅ Cliente já existe no Asaas:', cliente.asaas_customer_id);
      return cliente.asaas_customer_id;
    }

    // Tenta criar/sincronizar o cliente no Asaas
    try {
      console.log('🔄 Criando cliente no Asaas...');
      const response = await apiFetch('/api/asaas/customers', {
        method: 'POST',
        body: JSON.stringify({
          name: cliente.name,
          cpfCnpj: cliente.cpf,
          email: cliente.email || '',
          phone: cliente.phone || '',
          address: cliente.address || '',
          addressNumber: cliente.addressNumber || '',
          complement: cliente.complement || '',
          province: cliente.province || '',
          city: cliente.cityName || cliente.cidade || '',
          state: cliente.state || '',
          country: 'BRA',
          postalCode: cliente.postalCode || ''
        })
      });

      if (response.id) {
        console.log('✅ Cliente criado no Asaas:', response.id);
        return response.id;
      } else {
        throw new Error('Não foi possível criar cliente no Asaas');
      }
    } catch (err) {
      console.error('❌ Erro ao criar cliente no Asaas:', err);
      throw new Error('Cliente não está vinculado ao Asaas. Cadastre-o primeiro.');
    }
  };

  const resetForm = () => {
    setSelected(null);
    setForm(emptyForm);
    setDueDateDisplay(getTodayFormatted().display);
    setSubOpcao(null);
    setCategoria('plano');
    setTipoCobranca('indeterminada');
    setAsaasPaymentType('BOLETO');
    setClienteSelecionado(null);
    setSearchQuery('');
    setProdutoCpfl('');
    setAnexosCpfl([]);
  };

  const handleGerarCobrancaAsaas = async () => {
    if (!form.dueDate) {
      alert('Informe uma data de vencimento');
      throw new Error('SEM_DUE_DATE');
    }

    const asaasCustomerId = await ensureCustomerInAsaas(clienteSelecionado);

    const isParcelado = tipoCobranca === 'determinada' && form.parcelas && parseInt(form.parcelas) > 1;
    const valorTotal = parseFloat(form.amount);

    const payload = {
      customer: asaasCustomerId,
      billingType: asaasPaymentType,
      dueDate: form.dueDate,
      description: form.description || `Cobrança ${tipoCobranca}`,
    };

    if (isParcelado) {
      payload.totalValue = valorTotal;
      payload.installmentCount = parseInt(form.parcelas);
    } else {
      payload.value = valorTotal;
    }

    console.log('🚀 Enviando payload para Asaas:', payload);
    const result = await createAsaasPayment(payload);

    if (result.error) {
      throw new Error(result.error);
    }

    console.log('✅ Cobrança criada:', result);
  };

  const handleGerarCobrancaCpfl = async () => {
    const valorTotal = parseFloat(form.amount);
    const isParcelado = tipoCobranca === 'determinada' && form.parcelas && parseInt(form.parcelas) > 1;

    const formData = new FormData();
    formData.append('instalacao', clienteSelecionado.matricula);
    formData.append('pn', clienteSelecionado.pn);
    formData.append('valor', valorTotal.toFixed(2));
    formData.append('qtdParcelas', isParcelado ? String(parseInt(form.parcelas)) : '1');
    formData.append('produto', produtoCpfl);
    formData.append('identificacao', gerarIdentificacaoCpfl(clienteSelecionado.cpf));
    formData.append('parcelaIndeterminada', tipoCobranca === 'indeterminada' ? '1' : '0');

    // Dados do cliente — não usados pela CPFL, mas necessários para exibir
    // a cobrança na tela "Cobranças CPFL" (a CPFL não devolve isso em lugar nenhum).
    formData.append('clienteId', clienteSelecionado.id || '');
    formData.append('clienteNome', clienteSelecionado.name || '');
    formData.append('clienteCpf', clienteSelecionado.cpf || '');
    formData.append('produtoLabel', produtosCpfl.find((p) => p.codigo === produtoCpfl)?.label || produtoCpfl);
    formData.append('mesReferencia', form.dueDate ? form.dueDate.slice(0, 7).split('-').reverse().join('/') : '');

    anexosCpfl.filter((a) => a.enviar).forEach((a) => formData.append('anexos', a.file));

    console.log('🚀 Enviando cobrança CPFL:', {
      instalacao: clienteSelecionado.matricula,
      pn: clienteSelecionado.pn,
      valor: valorTotal.toFixed(2),
      produto: produtoCpfl,
      anexos: anexosCpfl.filter((a) => a.enviar).map((a) => a.file.name),
    });

    const result = await createCpflCharge(formData);
    console.log('✅ Cobrança CPFL registrada:', result);
  };

  const handleGerarCobranca = async () => {
    setLoading(true);
    try {
      const wasCpfl = selected === 'cpfl';

      if (selected === 'asaas') {
        await handleGerarCobrancaAsaas();
      } else if (selected === 'cpfl') {
        await handleGerarCobrancaCpfl();
      } else {
        // Pix Santander: ainda não implementado
        alert('Geração de cobrança via Pix Santander ainda não está disponível.');
        setLoading(false);
        return;
      }

      setShowModal(false);
      resetForm();
      window.scrollTo({ top: 0, behavior: 'smooth' });

      if (wasCpfl) {
        // CPFL: tela de sucesso animada e, ao final, vai direto para
        // a listagem de Cobranças CPFL.
        setShowSuccessCpfl(true);
        setTimeout(() => {
          setShowSuccessCpfl(false);
          navigate('/cobrancas/cpfl');
        }, 2200);
      } else {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 6000);
      }
    } catch (err) {
      if (err.message === 'SEM_DUE_DATE') { setLoading(false); return; }
      console.error('❌ Erro ao gerar cobrança:', err);
      alert(`Erro ao gerar cobrança: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="charges-container">
      {showSuccessCpfl && (
        <div className="gc-success-overlay">
          <div className="gc-success-box">
            <div className="gc-success-rings">
              <span className="gc-ring gc-ring-1" />
              <span className="gc-ring gc-ring-2" />
              <span className="gc-success-check"><Check size={34} strokeWidth={3} /></span>
            </div>
            <h2>Cobrança CPFL gerada com sucesso!</h2>
            <p>Redirecionando para Cobranças CPFL...</p>
          </div>
        </div>
      )}

      <div className="charges-page-header">
        <h1>Gerar Cobrança</h1>
      </div>

      {success && (
        <div className="alert-success">
          <Check size={16} /> Cobrança gerada com sucesso!
        </div>
      )}

      <div className="charges-card">
        <h2 className="charges-card-title">Gerar Cobrança</h2>
        <p className="charges-card-subtitle">Selecione a modalidade de cobrança</p>
        {avisoModalidade && <div className="gc-aviso-modalidade" role="alert">{avisoModalidade}</div>}

        <div className="payment-options-grid">
          {metodosVisiveis.map((m) => (
            <div
              key={m.id}
              className={`payment-option ${selected === m.id ? 'active' : ''}`}
              onClick={() => { setSelected(m.id); setErrors({}); }}
            >
              <m.Icon size={40} className={m.iconClass} />
              <span className="payment-option-label">{m.label}</span>
              <span className="payment-option-desc">{m.desc}</span>
            </div>
          ))}
        </div>

        {selected === 'pix' && (
          <PixCobrancaTabs
            apiFetch={apiFetch}
            initialClient={location.state && location.state.client ? location.state.client : null}
            onOpenList={() => navigate('/cobrancas/pix')}
          />
        )}

        {selected && selected !== 'pix' && (
          <form onSubmit={handleProximo} className="charges-form">
            <h3 className="form-modality-title">
              Gerar cobrança via {methods.find((m) => m.id === selected)?.label}
            </h3>

            {selected === 'asaas' && (
              <div className="form-row">
                <div className="form-field" style={{ flex: 1 }}>
                  <label className="asaas-type-label">
                    Tipo de Pagamento
                  </label>
                  <div className="asaas-payment-types">
                    {asaasPaymentTypes.map((type) => (
                      <button
                        key={type.id}
                        type="button"
                        className={`asaas-type-btn ${asaasPaymentType === type.id ? 'active' : ''}`}
                        onClick={() => setAsaasPaymentType(type.id)}
                      >
                        <type.icon size={16} />
                        <span>{type.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="form-row client-search-row">
              <div className="form-field client-search-field">
                <label className="client-search-label">
                  1️⃣ Busque o Cliente *
                </label>
                <div className="client-search-input-group">
                  <input
                    type="text"
                    placeholder="Digite nome, CPF ou matrícula"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setShowSearchResults(false);
                    }}
                    className={`client-search-input ${errors.client ? 'input-error' : ''}`}
                  />
                  <button
                    type="button"
                    className="btn-search-client"
                    onClick={handleBuscarCliente}
                    disabled={searching}
                  >
                    <Search size={16} />
                    {searching ? 'Buscando...' : 'Buscar'}
                  </button>
                </div>
                {errors.client && (
                  <span className="error-msg">
                    <AlertCircle size={12}/> {errors.client}
                  </span>
                )}
                {!clienteSelecionado && !errors.client && (
                  <span className="client-search-warning">
                    ⚠️ É necessário buscar e selecionar um cliente antes de continuar
                  </span>
                )}

                {showSearchResults && searchResults.length > 0 && (
                  <div className="search-results-dropdown">
                    {searchResults.map((cliente) => {
                      const normalized = normalizeCliente(cliente);
                      return (
                        <div
                          key={cliente.id}
                          onClick={() => handleSelecionarCliente(cliente)}
                          className="search-result-item"
                        >
                          <div className="search-result-name">
                            {normalized.name}
                          </div>
                          <div className="search-result-info">
                            CPF: {normalized.cpf} • {normalized.cidade}
                          </div>
                          <div className="search-result-type">
                            {normalized.type?.toUpperCase()} • {normalized.plano || 'Sem plano'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {clienteSelecionado && (
                  <div
                    className="alert-prefill"
                    style={{ background: '#ecfdf5', border: '1px solid #10b981', justifyContent: 'space-between', marginTop: 10, marginBottom: 0 }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                      <Check size={15} style={{ flexShrink: 0 }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        Cliente <strong>{clienteSelecionado.name}</strong> selecionado
                        {clienteSelecionado.type && <> · Modalidade <strong>{clienteSelecionado.type}</strong></>}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setClienteSelecionado(null);
                        setSearchQuery('');
                        setSelected(null);
                      }}
                      style={{
                        flex: '0 0 auto',
                        width: 'auto',
                        marginLeft: '10px',
                        padding: '4px 12px',
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 600,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Remover
                    </button>
                  </div>
                )}
              </div>

              <div className="form-field form-field-shrink">
                <div className="gc-toggle-wrap">
                  <button type="button" className={`gc-toggle-btn ${categoria === 'plano' ? 'active' : ''}`}
                    onClick={() => { setCategoria('plano'); setSubOpcao(null); setErrors((p) => ({ ...p, subOpcao: null })); }}>
                    Plano
                  </button>
                  <button type="button" className={`gc-toggle-btn ${categoria === 'atendimento' ? 'active' : ''}`}
                    onClick={() => { setCategoria('atendimento'); setSubOpcao(null); setErrors((p) => ({ ...p, subOpcao: null })); }}>
                    Atendimento
                  </button>
                </div>
              </div>
            </div>

            {categoria === 'plano' && (
              <div className="gc-plano-info">
                <Check size={13} style={{ color: '#059669', flexShrink: 0 }} />
                O plano será buscado automaticamente pelo cadastro do cliente.
              </div>
            )}

            {categoria === 'atendimento' && (
              <div className="gc-subopcoes-row">
                {opcoesAtendimento.map((op) => (
                  <button key={op.id} type="button"
                    className={`gc-subopcao-btn ${subOpcao === op.id ? 'selected' : ''}`}
                    onClick={() => { setSubOpcao(op.id); setErrors((p) => ({ ...p, subOpcao: null })); }}>
                    <op.Icon size={14} /> {op.label}
                  </button>
                ))}
                {errors.subOpcao && <span className="error-msg"><AlertCircle size={12}/> {errors.subOpcao}</span>}
              </div>
            )}

            {selected === 'cpfl' && (
              <>
                <div className="form-row">
                  <div className="form-field" style={{ flex: 1 }}>
                    <label className="asaas-type-label">Produto CPFL *</label>
                    <select
                      value={produtoCpfl}
                      onChange={(e) => setProdutoCpfl(e.target.value)}
                      className={errors.produtoCpfl ? 'input-error' : ''}
                      style={{ width: '100%', padding: '10px', borderRadius: 8, border: '1px solid #e2e8f0' }}
                    >
                      <option value="">Selecione o produto...</option>
                      {produtosCpfl.map((p) => (
                        <option key={p.codigo} value={p.codigo}>{p.label}</option>
                      ))}
                    </select>
                    {errors.produtoCpfl && <span className="error-msg"><AlertCircle size={12}/> {errors.produtoCpfl}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field" style={{ flex: 1 }}>
                    <label className="asaas-type-label">Documentos (obrigatório) *</label>
                    <label
                      htmlFor="cpfl-anexos-input"
                      className="btn-search-client"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', width: 'fit-content' }}
                    >
                      <Upload size={16} /> Adicionar arquivos
                    </label>
                    <input
                      id="cpfl-anexos-input"
                      type="file"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png"
                      style={{ display: 'none' }}
                      onChange={handleAdicionarAnexos}
                    />
                    {errors.anexos && <span className="error-msg"><AlertCircle size={12}/> {errors.anexos}</span>}

                    {anexosCpfl.length > 0 && (
                      <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {anexosCpfl.map((anexo, idx) => (
                          <div
                            key={`${anexo.file.name}-${idx}`}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 10,
                              padding: '10px 12px',
                              background: '#f9fafb',
                              border: '1px solid #e5e7eb',
                              borderRadius: 8,
                              flexWrap: 'wrap'
                            }}
                                                      >
                              <a
                                href={anexo.previewUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ fontSize: 13, color: '#4f46e5', textDecoration: 'underline', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: '1 1 160px', minWidth: 0 }}
                              >
                                {anexo.file.name}
                              </a>
                              <select

                              value={anexo.tipo}
                              onChange={(e) => handleTipoAnexoChange(idx, e.target.value)}
                              style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, flexShrink: 0 }}
                            >
                              {tipoDocumentoOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', flexShrink: 0 }}>
                              <input
                                type="checkbox"
                                checked={anexo.enviar}
                                onChange={(e) => handleEnviarAnexoChange(idx, e.target.checked)}
                              />
                              Enviar
                            </label>
                            <button
                              type="button"
                              onClick={() => handleRemoverAnexo(idx)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', flexShrink: 0, padding: 4 }}
                              title="Remover"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            <div className="form-row form-row-valor">
              <div className="form-field">
                <input type="number" step="0.01" placeholder="Valor (R$)"
                  value={form.amount} onChange={(e) => handleChange('amount', e.target.value)}
                  className={errors.amount ? 'input-error' : ''} />
                {errors.amount && <span className="error-msg"><AlertCircle size={12}/> {errors.amount}</span>}
              </div>

              <div className="form-field form-field-shrink">
                <div className="gc-toggle-wrap">
                  <button type="button" className={`gc-toggle-btn ${tipoCobranca === 'indeterminada' ? 'active' : ''}`}
                    onClick={() => { setTipoCobranca('indeterminada'); handleChange('parcelas', ''); }}>
                    <Repeat size={13} /> Recorrente
                  </button>
                  <button type="button" className={`gc-toggle-btn ${tipoCobranca === 'determinada' ? 'active' : ''}`}
                    onClick={() => setTipoCobranca('determinada')}>
                    <Calendar size={13} /> Parcelado
                  </button>
                </div>
              </div>

              {tipoCobranca === 'determinada' && (
                <div className="form-field form-field-parcelas">
                  <input type="number" min="1" placeholder="Qtd. meses"
                    value={form.parcelas} onChange={(e) => handleChange('parcelas', e.target.value)}
                    className={errors.parcelas ? 'input-error' : ''} />
                  {errors.parcelas && <span className="error-msg"><AlertCircle size={12}/> {errors.parcelas}</span>}
                </div>
              )}
            </div>

            {form.amount && (
              <div className="gc-cobranca-info">
                {tipoCobranca === 'indeterminada'
                  ? <><Repeat size={13}/> Cobrança recorrente de <strong>R$ {parseFloat(form.amount).toFixed(2)}</strong> por mês, sem data de encerramento.</>
                  : form.parcelas
                    ? <><Calendar size={13}/> Cobrança parcelada de <strong>R$ {parseFloat(form.amount).toFixed(2)}</strong> por <strong>{form.parcelas} {Number(form.parcelas) === 1 ? 'parcela' : 'parcelas'}</strong>.</>
                    : null}
              </div>
            )}

            <div className="form-row">
              <div className="form-field">
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="dd/mm/aaaa"
                  value={dueDateDisplay}
                  onChange={handleDueDateChange}
                  maxLength={10}
                />
              </div>
              <div className="form-field">
                <input type="text" placeholder="Descrição" value={form.description}
                  onChange={(e) => handleChange('description', e.target.value)} />
              </div>
            </div>

            <button
              type="submit"
              className="btn-submit"
              style={{ width: 'auto', minWidth: 180, padding: '12px 32px', alignSelf: 'flex-end', display: 'block', marginLeft: 'auto' }}
            >
              Próximo →
            </button>
          </form>
        )}
      </div>

      {showModal && (
        <div className="gc-overlay" onClick={() => setShowModal(false)}>
          <div className="gc-modal" onClick={(e) => e.stopPropagation()}>

            <div className="gc-modal-header">
              <div className="gc-modal-header-left">
                <FileText size={18} style={{ color: '#4f46e5' }} />
                <h3>Confirmar Cobrança</h3>
              </div>
              <button className="gc-modal-close" onClick={() => setShowModal(false)}>
                <X size={18} />
              </button>
            </div>

            <div className="gc-modal-body">
              <div className="gc-modal-section-title"><User size={13} /> Dados do Cliente</div>
              <div className="gc-cliente-grid">
                <div className="gc-cliente-item">
                  <span className="gc-cliente-label">Nome</span>
                  <span className="gc-cliente-value">{clienteSelecionado?.name || '—'}</span>
                </div>
                <div className="gc-cliente-item">
                  <span className="gc-cliente-label">CPF</span>
                  <span className="gc-cliente-value">{clienteSelecionado?.cpf || '—'}</span>
                </div>
                <div className="gc-cliente-item">
                  <span className="gc-cliente-label">Matrícula</span>
                  <span className="gc-cliente-value">{clienteSelecionado?.matricula || '—'}</span>
                </div>
                {selected === 'cpfl' && (
                  <>
                    <div className="gc-cliente-item">
                      <span className="gc-cliente-label">UC (Instalação)</span>
                      <span className="gc-cliente-value">{clienteSelecionado?.matricula || '—'}</span>
                    </div>
                    <div className="gc-cliente-item">
                      <span className="gc-cliente-label">PN</span>
                      <span className="gc-cliente-value">{clienteSelecionado?.pn || '—'}</span>
                    </div>
                  </>
                )}
                <div className="gc-cliente-item">
                  <span className="gc-cliente-label">Cidade</span>
                  <span className="gc-cliente-value">
                    <MapPin size={12}/> {clienteSelecionado?.cidade || '—'}
                  </span>
                </div>
              </div>

              <div className="gc-modal-section-title" style={{ marginTop: 18 }}>
                <Hash size={13} /> Resumo da Cobrança
              </div>
              <div className="gc-resumo-grid">
                <div className="gc-resumo-item">
                  <span className="gc-cliente-label">Modalidade</span>
                  <span className="gc-cliente-value">{methods.find((m) => m.id === selected)?.label}</span>
                </div>

                {selected === 'asaas' && (
                  <div className="gc-resumo-item">
                    <span className="gc-cliente-label">Tipo de Pagamento</span>
                    <span className="gc-cliente-value">
                      {asaasPaymentTypes.find(t => t.id === asaasPaymentType)?.label}
                    </span>
                  </div>
                )}

                {selected === 'cpfl' && (
                  <div className="gc-resumo-item">
                    <span className="gc-cliente-label">Produto</span>
                    <span className="gc-cliente-value">
                      {produtosCpfl.find(p => p.codigo === produtoCpfl)?.label || produtoCpfl || '—'}
                    </span>
                  </div>
                )}

                <div className="gc-resumo-item">
                  <span className="gc-cliente-label">Vencimento</span>
                  <span className="gc-cliente-value">
                    <Calendar size={12} style={{ marginRight: 4 }} />
                    {formatDate(form.dueDate)}
                  </span>
                </div>
                <div className="gc-resumo-item">
                  <span className="gc-cliente-label">Valor</span>
                  <span className="gc-cliente-value gc-valor">R$ {parseFloat(form.amount || 0).toFixed(2)}</span>
                </div>
                <div className="gc-resumo-item">
                  <span className="gc-cliente-label">Tipo</span>
                  <span className="gc-cliente-value">
                    {tipoCobranca === 'indeterminada'
                      ? 'Recorrente'
                      : `Parcelado — ${form.parcelas} ${Number(form.parcelas) === 1 ? 'mês' : 'meses'}`}
                  </span>
                </div>
                {categoria === 'atendimento' && subOpcao && (
                  <div className="gc-resumo-item">
                    <span className="gc-cliente-label">Atendimento</span>
                    <span className="gc-cliente-value">{opcoesAtendimento.find((o) => o.id === subOpcao)?.label}</span>
                  </div>
                )}
                {categoria === 'plano' && (
                  <div className="gc-resumo-item">
                    <span className="gc-cliente-label">Plano</span>
                    <span className="gc-cliente-value">{clienteSelecionado?.plano || '—'}</span>
                  </div>
                )}
              </div>

              {selected === 'cpfl' && (
                <>
                  <div className="gc-modal-section-title" style={{ marginTop: 18 }}>
                    <FileText size={13} /> Documentos que serão enviados
                  </div>
                  <div className="gc-arquivos-list">
                    {anexosCpfl.map((anexo, idx) => (
                      <div key={`${anexo.file.name}-${idx}`} className={`gc-arquivo-item ${anexo.enviar ? 'checked' : 'unchecked'}`}>
                        <div className="gc-arquivo-check">
                          {anexo.enviar
                            ? <CheckSquare size={16} style={{ color: '#4f46e5' }} />
                            : <Square size={16} style={{ color: '#cbd5e1' }} />}
                        </div>
                        <div className="gc-arquivo-icon">
                          <FileText size={18} style={{ color: '#e11d48' }} />
                        </div>
                        <div className="gc-arquivo-info">
                          <div className="gc-arquivo-nome">{anexo.file.name}</div>
                          <div className="gc-arquivo-meta">
                            {formatBytes(anexo.file.size)} · {tipoDocumentoOptions.find(t => t.value === anexo.tipo)?.label}
                          </div>
                        </div>
                        <div className={`gc-arquivo-status ${anexo.enviar ? 'status-enviar' : 'status-nao-enviar'}`}>
                          {anexo.enviar ? 'Enviar' : 'Não enviar'}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="gc-arquivos-count-row">
                    {anexosCpfl.filter((a) => a.enviar).length} de {anexosCpfl.length} documento(s) selecionado(s) para envio
                  </div>
                </>
              )}
            </div>

            <div className="gc-modal-footer">
              <button className="gc-btn-cancel" onClick={() => setShowModal(false)}>Voltar</button>
              <button
                className="gc-btn-gerar"
                onClick={handleGerarCobranca}
                disabled={loading || (selected === 'cpfl' && anexosCpfl.filter((a) => a.enviar).length === 0)}
              >
                {loading ? 'Gerando...' : <><Check size={15} /> Gerar Cobrança</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
